// src/views/folders/hooks/useTreeSearch.ts
import {
  useState, useEffect, useMemo, useRef, useCallback,
} from 'react';
import { TreeApi } from 'react-arborist';
import { TreeNodeType } from '../../../services/ApiService';
import { matchesSearch, toSearchWords } from '../../../utils/search';

interface TreeNodeWithSearch extends TreeNodeType {
  isSearchMatch?: boolean;
}

// Cache dla wyników filtrowania
const filterCache = new Map<string, TreeNodeWithSearch[]>();
const MAX_CACHE_SIZE = 50;

interface UseTreeSearchOptions {
  sortBy?: 'createdAt' | 'name';
  sortOrder?: 'asc' | 'desc';
}

// ✅ Usuń parametr optimisticItems, przenieś options na 3. pozycję
export const useTreeSearch = (
  folderTreeData: TreeNodeType[],
  treeRef: React.RefObject<TreeApi<TreeNodeType>>,
  options: UseTreeSearchOptions = {}, // ✅ Teraz to jest 3. parametr
) => {
  const {
    sortBy = 'createdAt',
    sortOrder = 'desc',
  } = options;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchExpandedFolders, setSearchExpandedFolders] = useState<Set<string>>(new Set());

  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Funkcja sortowania węzłów
  const sortNodes = useCallback((nodes: TreeNodeType[]): TreeNodeType[] => {
    const sorted = [...nodes].sort((a, b) => {
      // Zawsze foldery przed listami
      if (a.type !== b.type) {
        return a.type === 'folder' ? -1 : 1;
      }

      // Sortowanie według wybranego kryterium
      let comparison = 0;

      switch (sortBy) {
        case 'name': {
          comparison = a.name.localeCompare(b.name, 'pl');
          break;
        }
        case 'createdAt':
        default: {
          const dateA = new Date(a.data?.createdAt || a.updatedAt || 0).getTime();
          const dateB = new Date(b.data?.createdAt || b.updatedAt || 0).getTime();
          comparison = dateB - dateA;
          break;
        }
      }

      if (sortBy !== 'name' && sortOrder === 'asc') {
        comparison = -comparison;
      }

      return comparison;
    });

    return sorted.map((node) => ({
      ...node,
      children: node.children ? sortNodes(node.children) : [],
    }));
  }, [sortBy, sortOrder]);

  // ❌ USUŃ całą funkcję mergeOptimisticItems - nie jest już potrzebna!

  // Optymalizowana funkcja filtrowania
  const filterNodes = useCallback((
    nodes: TreeNodeType[],
    searchTerm: string,
  ): TreeNodeWithSearch[] => {
    // Ta sama reguła co na ekranie katalogu: znaki inne niż litery i cyfry nie przeszkadzają
    const searchWords = toSearchWords(searchTerm);

    return nodes.reduce<TreeNodeWithSearch[]>((acc, node) => {
      const isMatch = matchesSearch(node.name, searchWords);

      const filteredChildren = node.children
        ? filterNodes(node.children, searchTerm)
        : [];

      if (isMatch || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren,
          ...(isMatch && { isSearchMatch: true }),
        });
      }

      return acc;
    }, []);
  }, []);

  // ✅ Uproszczone filtrowanie - bez mergeOptimisticItems
  const filteredTree = useMemo(() => {
    // ✅ Po prostu sortuj dane z Redux
    const sortedTree = sortNodes(folderTreeData);

    if (!debouncedSearchQuery) {
      filterCache.clear();

      return sortedTree;
    }

    // Sprawdź cache
    const cacheKey = `${debouncedSearchQuery}_${sortedTree.length}_${sortBy}_${sortOrder}`;
    if (filterCache.has(cacheKey)) {
      return filterCache.get(cacheKey)!;
    }

    // Filtruj
    const result = filterNodes(sortedTree, debouncedSearchQuery);

    // Zarządzaj rozmiarem cache
    if (filterCache.size >= MAX_CACHE_SIZE) {
      const firstKey = filterCache.keys().next().value;
      if (firstKey) {
        filterCache.delete(firstKey);
      }
    }

    filterCache.set(cacheKey, result);

    return result;
  }, [
    folderTreeData,
    debouncedSearchQuery,
    sortBy,
    sortOrder,
    filterNodes,
    sortNodes,
  ]);

  // Efekt rozwijania folderów przy wyszukiwaniu
  useEffect(() => {
    if (!debouncedSearchQuery || !treeRef.current) {
      setSearchExpandedFolders(new Set());

      return undefined;
    }

    const expandedSet = new Set<string>();
    const searchWords = toSearchWords(debouncedSearchQuery);

    const findMatchingPaths = (
      nodes: TreeNodeType[],
      currentPath: string[] = [],
    ): void => {
      nodes.forEach((node) => {
        const isMatch = matchesSearch(node.name, searchWords);

        if (isMatch) {
          currentPath.forEach((folderId) => {
            if (folderId.startsWith('folder-')) {
              expandedSet.add(folderId);
            }
          });
        }

        if (node.children && node.type === 'folder') {
          findMatchingPaths(node.children, [...currentPath, node.id]);
        }
      });
    };

    findMatchingPaths(filteredTree);
    setSearchExpandedFolders(expandedSet);

    const rafId = requestAnimationFrame(() => {
      expandedSet.forEach((folderId) => {
        const node = treeRef.current?.get(folderId);
        if (node && !node.isOpen) {
          node.open();
        }
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [debouncedSearchQuery, filteredTree, treeRef]);

  const resetSearch = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setSearchExpandedFolders(new Set());
    filterCache.clear();
  }, []);

  const clearCache = useCallback(() => {
    filterCache.clear();
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    filteredTree,
    searchExpandedFolders,
    resetSearch,
    clearCache,
    isSearching: !!debouncedSearchQuery,
    hasResults: filteredTree.length > 0,
  };
};

// mohikan
// // src/views/folders/hooks/useTreeSearch.ts
// import {
//   useState, useEffect, useMemo, useRef, useCallback,
// } from 'react';
// import { TreeApi } from 'react-arborist';
// import { TreeNodeType } from '../../../services/ApiService';

// interface TreeNodeWithSearch extends TreeNodeType {
//   isSearchMatch?: boolean;
// }

// // Cache dla wyników filtrowania
// const filterCache = new Map<string, TreeNodeWithSearch[]>();
// const MAX_CACHE_SIZE = 50;

// interface UseTreeSearchOptions {
//   sortBy?: 'createdAt' | 'name';
//   sortOrder?: 'asc' | 'desc';
// }

// export const useTreeSearch = (
//   folderTreeData: TreeNodeType[],
//   treeRef: React.RefObject<TreeApi<TreeNodeType>>,
//   optimisticItems: TreeNodeType[] = [],
//   options: UseTreeSearchOptions = {},
// ) => {
//   const {
//     sortBy = 'createdAt',
//     sortOrder = 'desc',
//   } = options;

//   const [searchQuery, setSearchQuery] = useState('');
//   const [searchExpandedFolders, setSearchExpandedFolders] = useState<Set<string>>(new Set());

//   const searchTimeoutRef = useRef<NodeJS.Timeout>();
//   const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

//   useEffect(() => {
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }

//     searchTimeoutRef.current = setTimeout(() => {
//       setDebouncedSearchQuery(searchQuery);
//     }, 300);

//     return () => {
//       if (searchTimeoutRef.current) {
//         clearTimeout(searchTimeoutRef.current);
//       }
//     };
//   }, [searchQuery]);

//   // Funkcja sortowania węzłów
//   const sortNodes = useCallback((nodes: TreeNodeType[]): TreeNodeType[] => {
//     const sorted = [...nodes].sort((a, b) => {
//       // Zawsze foldery przed listami
//       if (a.type !== b.type) {
//         return a.type === 'folder' ? -1 : 1;
//       }

//       // Sortowanie według wybranego kryterium
//       let comparison = 0;

//       switch (sortBy) {
//         case 'name': {
//           comparison = a.name.localeCompare(b.name, 'pl');
//           break;
//         }
//         case 'createdAt':
//         default: {
//           // Używamy data?.createdAt lub updatedAt jako fallback
//           const dateA = new Date(a.data?.createdAt || a.updatedAt || 0).getTime();
//           const dateB = new Date(b.data?.createdAt || b.updatedAt || 0).getTime();
//           comparison = dateB - dateA; // Najnowsze najpierw
//           break;
//         }
//       }

//       // Odwróć kolejność jeśli sortOrder === 'asc' (ale tylko dla dat)
//       if (sortBy !== 'name' && sortOrder === 'asc') {
//         comparison = -comparison;
//       }

//       return comparison;
//     });

//     // Rekurencyjnie sortuj dzieci
//     return sorted.map((node) => ({
//       ...node,
//       children: node.children ? sortNodes(node.children) : [],
//     }));
//   }, [sortBy, sortOrder]);

//   // Funkcja łącząca optymistyczne elementy z rzeczywistymi danymi
//   const mergeOptimisticItems = useCallback((
//     nodes: TreeNodeType[],
//     optimistic: TreeNodeType[],
//   ): TreeNodeType[] => {
//     if (optimistic.length === 0) return sortNodes(nodes);

//     // Klonuj głęboką strukturę drzewa
//     const clonedNodes = JSON.parse(JSON.stringify(nodes));

//     // Dla każdego optymistycznego elementu
//     optimistic.forEach((item) => {
//       if (item.parentId) {
//       // Funkcja rekurencyjna, która zwraca nową tablicę ze zaktualizowanym węzłem
//         const addToParent = (nodeList: TreeNodeType[]): TreeNodeType[] => nodeList.map((nodeItem) => {
//           if (nodeItem.id === item.parentId) {
//             // Znaleźliśmy rodzica - dodaj dziecko
//             return {
//               ...nodeItem,
//               children: [item, ...(nodeItem.children || [])],
//             };
//           }

//           // Jeśli to nie ten węzeł, ale ma dzieci, rekurencyjnie je przeszukaj
//           if (nodeItem.children && nodeItem.children.length > 0) {
//             return {
//               ...nodeItem,
//               children: addToParent(nodeItem.children),
//             };
//           }

//           // Zwróć węzeł bez zmian
//           return nodeItem;
//         });

//         // Zastąp clonedNodes nową wersją ze zaktualizowanym drzewem
//         const updatedNodes = addToParent(clonedNodes);
//         // Używamy splice do zastąpienia całej zawartości tablicy
//         clonedNodes.splice(0, clonedNodes.length, ...updatedNodes);
//       } else {
//       // Element najwyższego poziomu - dodaj na początek
//         clonedNodes.unshift(item);
//       }
//     });

//     return sortNodes(clonedNodes);
//   }, [sortNodes]);

//   // Optymalizowana funkcja filtrowania
//   const filterNodes = useCallback((
//     nodes: TreeNodeType[],
//     searchTerm: string,
//   ): TreeNodeWithSearch[] => {
//     const searchLower = searchTerm.toLowerCase();
//     const searchWords = searchLower.split(/\s+/).filter(Boolean);

//     return nodes.reduce<TreeNodeWithSearch[]>((acc, node) => {
//       // Sprawdź czy wszystkie słowa występują w nazwie
//       const matchesSearch = searchWords.every((word) => node.name.toLowerCase().includes(word));

//       // Rekurencyjnie filtruj dzieci
//       const filteredChildren = node.children
//         ? filterNodes(node.children, searchTerm)
//         : [];

//       if (matchesSearch || filteredChildren.length > 0) {
//         acc.push({
//           ...node,
//           children: filteredChildren,
//           ...(matchesSearch && { isSearchMatch: true }), // Dodaj flagę tylko jeśli pasuje
//         });
//       }

//       return acc;
//     }, []);
//   }, []);

//   // Memoizowane filtrowanie z cache, sortowaniem i optymistycznymi elementami
//   const filteredTree = useMemo(() => {
//     // Najpierw połącz z optymistycznymi elementami
//     const mergedTree = mergeOptimisticItems(folderTreeData, optimisticItems);

//     if (!debouncedSearchQuery) {
//       filterCache.clear();

//       return mergedTree;
//     }

//     // Sprawdź cache
//     const cacheKey = `${debouncedSearchQuery}_${mergedTree.length}_${sortBy}_${sortOrder}`;
//     if (filterCache.has(cacheKey)) {
//       return filterCache.get(cacheKey)!;
//     }

//     // Filtruj
//     const result = filterNodes(mergedTree, debouncedSearchQuery);

//     // Zarządzaj rozmiarem cache
//     if (filterCache.size >= MAX_CACHE_SIZE) {
//       const firstKey = filterCache.keys().next().value;
//       if (firstKey) {
//         filterCache.delete(firstKey);
//       }
//     }

//     filterCache.set(cacheKey, result);

//     return result;
//   }, [
//     folderTreeData,
//     debouncedSearchQuery,
//     optimisticItems,
//     sortBy,
//     sortOrder,
//     filterNodes,
//     mergeOptimisticItems,
//   ]);

//   // Efekt rozwijania folderów przy wyszukiwaniu
//   useEffect(() => {
//     if (!debouncedSearchQuery || !treeRef.current) {
//       setSearchExpandedFolders(new Set());

//       return undefined;
//     }

//     const expandedSet = new Set<string>();
//     const searchLower = debouncedSearchQuery.toLowerCase();
//     const searchWords = searchLower.split(/\s+/).filter(Boolean);

//     // Rekurencyjna funkcja znajdująca ścieżki
//     const findMatchingPaths = (
//       nodes: TreeNodeType[],
//       currentPath: string[] = [],
//     ): void => {
//       nodes.forEach((node) => {
//         const matchesSearch = searchWords.every((word) => node.name.toLowerCase().includes(word));

//         if (matchesSearch) {
//           currentPath.forEach((folderId) => {
//             if (folderId.startsWith('folder-')) {
//               expandedSet.add(folderId);
//             }
//           });
//         }

//         if (node.children && node.type === 'folder') {
//           findMatchingPaths(node.children, [...currentPath, node.id]);
//         }
//       });
//     };

//     findMatchingPaths(filteredTree);
//     setSearchExpandedFolders(expandedSet);

//     // Użyj RAF dla płynniejszej animacji
//     const rafId = requestAnimationFrame(() => {
//       expandedSet.forEach((folderId) => {
//         const node = treeRef.current?.get(folderId);
//         if (node && !node.isOpen) {
//           node.open();
//         }
//       });
//     });

//     // Return cleanup function
//     return () => {
//       cancelAnimationFrame(rafId);
//     };
//   }, [debouncedSearchQuery, filteredTree, treeRef]);

//   // Funkcja resetowania wyszukiwania
//   const resetSearch = useCallback(() => {
//     setSearchQuery('');
//     setDebouncedSearchQuery('');
//     setSearchExpandedFolders(new Set());
//     filterCache.clear();
//   }, []);

//   // Funkcja czyszczenia cache
//   const clearCache = useCallback(() => {
//     filterCache.clear();
//   }, []);

//   return {
//     searchQuery,
//     setSearchQuery,
//     debouncedSearchQuery,
//     filteredTree,
//     searchExpandedFolders,
//     resetSearch,
//     clearCache,
//     isSearching: !!debouncedSearchQuery,
//     hasResults: filteredTree.length > 0,
//   };
// };
